import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Explorer, { TreeControlDefaultState, TreeControlState } from 'components/explorer';
import {
  collectDescendantNodeIds,
  collectIndeterminateNodeIds,
} from 'components/archiving/utils';
import { TreeNodeDTO } from 'business/models/Models';
import { useSearchBarContext } from '../Context/SearchBarContext';
import styles from './AdvancedSearch.module.scss';
import AdvancedSearchTreeDataSupplier from 'business/tree/AdvancedSearchTreeDataSupplier';
import { ADVANCED_SEARCH_LOCATION_NODE_TYPES, shouldRenderLocationNode } from './utils';

interface LocationPickerProps {
  searchText: string;
}

export const LocationPicker = ({ searchText }: LocationPickerProps) => {
  const { filters, updateFilters } = useSearchBarContext();
  const [treeControlState, setTreeControlState] = useState<TreeControlState>({
    ...TreeControlDefaultState,
    checkedExplorerNodes: filters.locationTreeNodeIds,
  });

  useEffect(() => {
    setTreeControlState((prev) => ({
      ...prev,
      checkedExplorerNodes: filters.locationTreeNodeIds,
    }));
  }, [filters.locationTreeNodeIds]);

  const handleCheckboxChange = (nodeId: number) => {
    const isSelected = treeControlState.checkedExplorerNodes.includes(nodeId);
    const descendants = collectDescendantNodeIds(treeControlState.treenodes, nodeId);
    const checkedExplorerNodes = isSelected
      ? treeControlState.checkedExplorerNodes.filter(
          (id) => !new Set(descendants.concat([nodeId])).has(id),
        )
      : Array.from(
          new Set([
            ...treeControlState.checkedExplorerNodes,
            ...(descendants.length ? descendants : [nodeId]),
            nodeId,
          ]),
        );
    setTreeControlState({ ...treeControlState, checkedExplorerNodes });
    updateFilters({ locationTreeNodeIds: checkedExplorerNodes });
  };

  const explorerTreeControlState = useMemo(
    () => ({
      ...treeControlState,
      indeterminateExplorerNodes: collectIndeterminateNodeIds(
        treeControlState.treenodes,
        treeControlState.checkedExplorerNodes,
      ),
    }),
    [treeControlState],
  );

  const shouldRenderNode = useCallback(
    (node: TreeNodeDTO) => shouldRenderLocationNode(node, searchText),
    [searchText],
  );

  return (
    <div id="location-picker-wrapper" className={styles.locationPicker}>
      <div id="location-picker-tree-wrapper" className={styles.locationTreeWrapper}>
        <Explorer
          backgroundColor="#ffffff"
          showQuickAccessNode={false}
          dataSupplier={AdvancedSearchTreeDataSupplier}
          treeControlState={explorerTreeControlState}
          setTreeControlState={setTreeControlState}
          shouldRenderNode={shouldRenderNode}
          allowedRootTreeNodeTypes={ADVANCED_SEARCH_LOCATION_NODE_TYPES}
          shouldListenToBroadcastedEvents={false}
          onCheckboxChange={handleCheckboxChange}
          isSearchEnabled={true}
        />
      </div>
    </div>
  );
};
